import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const fadeDown = {
  hidden: { opacity: 0, y: -30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const stagger = { show: { transition: { staggerChildren: 0.1 } } };
function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      style={style}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={fadeUp}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}
const whyCards = [
  {
    icon: "🎨",
    title: "Creative Expression",
    desc: "Share your artwork freely in a space that celebrates every brushstroke, sculpture, and pixel — without judgment.",
    accent: "var(--gold)",
  },
  {
    icon: "🤝",
    title: "Genuine Connection",
    desc: "Meet artists who speak your language — color, form, and emotion. Build friendships that last a lifetime.",
    accent: "var(--coral)",
  },
  {
    icon: "📚",
    title: "Learn & Grow",
    desc: "Discover techniques, get feedback, join challenges, and evolve your craft surrounded by passionate creators.",
    accent: "var(--soft)",
  },
  {
    icon: "✨",
    title: "Inspire & Be Inspired",
    desc: "Art heals. In Artesa, every post you share could be the spark that ignites someone else's masterpiece.",
    accent: "#2ab5a8",
  },
];
const steps = [
  {
    num: "1",
    title: "Click the Link",
    desc: "Tap 'Join on WhatsApp' to reach the Artesa community invite instantly.",
  },
  {
    num: "2",
    title: "Introduce Yourself",
    desc: "Share your name, art form, and one thing that made you fall in love with creating.",
  },
  {
    num: "3",
    title: "Share Your Art",
    desc: "Post paintings, sculptures, digital art, sketches — anything that came from your soul.",
  },
  {
    num: "4",
    title: "Create. Connect.",
    desc: "Engage, collaborate, and grow. Artesa is alive because every artist shows up and creates.",
  },
];
const whoCards = [
  { emoji: "🖌️", label: "Artists" },
  { emoji: "🗿", label: "Sculptors" },
  { emoji: "✏️", label: "Illustrators" },
  { emoji: "📱", label: "Digital Artists" },
  { emoji: "🌿", label: "Art Healers" },
];
const stats = [
  { num: "380+", label: "Artists Joined" },
  { num: "∞", label: "Creativity Shared" },
  { num: "1", label: "Vibrant Community" },
  { num: "0", label: "Barriers to Art" },
];
const WA_LINK = "https://chat.whatsapp.com/KeBid8TitcHKNLhHRnQ8NM?mode=gi_t";
const S = {
  hero: {
    minHeight: "100vh",
    background: "linear-gradient(160deg,#1a0a2e 0%,#2e0a1a 40%,#0a1a2e 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "80px 5% 60px",
    position: "relative",
    overflow: "hidden",
  },
  heroBg: {
    position: "absolute",
    inset: 0,
    background: `
      radial-gradient(ellipse 60% 40% at 20% 30%, rgba(201,152,42,0.15) 0%, transparent 60%),
      radial-gradient(ellipse 40% 60% at 80% 70%, rgba(124,92,191,0.15) 0%, transparent 60%)
    `,
    pointerEvents: "none",
  },
  heroBadge: {
    fontFamily: "'Satisfy',cursive",
    color: "var(--gold,#c9982a)",
    fontSize: "1.2rem",
    letterSpacing: "3px",
    marginBottom: "20px",
  },
  heroTitle: {
    fontFamily: "'Cormorant Garamond',serif",
    fontSize: "clamp(3rem,8vw,6rem)",
    fontWeight: 700,
    fontStyle: "italic",
    color: "#fff",
    lineHeight: 1.1,
  },
  heroSub: {
    color: "rgba(255,255,255,0.68)",
    fontWeight: 300,
    fontSize: "clamp(1rem,2.5vw,1.25rem)",
    maxWidth: 560,
    lineHeight: 1.9,
    margin: "24px auto 0",
  },
  statsBar: {
    background: "#2a0a3e",
    padding: "40px 5%",
    display: "flex",
    justifyContent: "center",
    gap: "clamp(20px,5vw,80px)",
    flexWrap: "wrap",
  },
  statNum: {
    fontFamily: "'Cormorant Garamond',serif",
    fontSize: "2.8rem",
    fontWeight: 700,
    color: "var(--gold,#c9982a)",
  },
  statLabel: {
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.55)",
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginTop: "4px",
  },
  sectionWarm: {
    padding: "100px 5%",
    background: "linear-gradient(160deg,#f5ede0,#fff)",
  },
  sectionCream: {
    padding: "100px 5%",
    background: "var(--cream,#fdf8f0)",
  },
  sectionLabel: {
    fontFamily: "'Satisfy',cursive",
    fontSize: "1.1rem",
    color: "var(--coral,#e8503a)",
    letterSpacing: "2px",
    marginBottom: "12px",
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond',serif",
    fontSize: "clamp(2rem,5vw,3.5rem)",
    fontWeight: 700,
    fontStyle: "italic",
    color: "var(--ink,#1a0a1e)",
    lineHeight: 1.2,
  },
  whyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
    gap: "28px",
    marginTop: "60px",
  },
  whyCard: {
    background: "#fff",
    borderRadius: "24px",
    padding: "40px 32px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
    position: "relative",
    overflow: "hidden",
    cursor: "default",
  },
  quoteSec: {
    background: "linear-gradient(135deg,#2a0a3e,#1a0a2e)",
    padding: "100px 5%",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  quoteText: {
    fontFamily: "'Cormorant Garamond',serif",
    fontSize: "clamp(1.6rem,4vw,2.8rem)",
    fontStyle: "italic",
    fontWeight: 300,
    color: "#fff",
    maxWidth: 800,
    margin: "0 auto",
    lineHeight: 1.7,
  },
  quoteAuthor: {
    marginTop: "28px",
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.45)",
    letterSpacing: "3px",
    textTransform: "uppercase",
  },
  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: "20px",
    marginTop: "60px",
  },
  stepNum: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#c9982a,#f0c84a)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
    fontFamily: "'Cormorant Garamond',serif",
    fontSize: "2rem",
    fontWeight: 700,
    color: "#1a0a1e",
    boxShadow: "0 8px 24px rgba(201,152,42,0.35)",
  },
  whoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
    gap: "18px",
    marginTop: "60px",
  },
  whoCard: {
    background: "#fff",
    borderRadius: "20px",
    padding: "32px 16px",
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
    cursor: "default",
  },
  ctaFinal: {
    background: "linear-gradient(135deg,#1a0a2e 0%,#2e0a1a 100%)",
    padding: "120px 5%",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  artesaTag: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(201,152,42,0.3)",
    borderRadius: "50px",
    padding: "8px 24px",
    display: "inline-block",
    marginBottom: "28px",
    fontSize: "0.82rem",
    color: "var(--gold,#c9982a)",
    letterSpacing: "3px",
    textTransform: "uppercase",
  },
  waBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    padding: "18px 48px",
    borderRadius: "50px",
    background: "linear-gradient(135deg,#25D366,#128C7E)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "1.1rem",
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 10px 40px rgba(37,211,102,0.4)",
  },
};
function Career() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -60 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
    >
      <section style={S.hero}>
        <div style={S.heroBg} />
        {[
          {
            w: 300,
            h: 300,
            bg: "rgba(201,152,42,0.06)",
            top: "-80px",
            right: "-80px",
            delay: 0,
          },
          {
            w: 200,
            h: 200,
            bg: "rgba(124,92,191,0.08)",
            bottom: "15%",
            left: "-60px",
            delay: 2,
          },
          {
            w: 150,
            h: 150,
            bg: "rgba(232,80,58,0.06)",
            top: "40%",
            right: "5%",
            delay: 4,
          },
        ].map((b, i) => (
          <motion.div
            key={i}
            animate={{ y: [0, -25, 0], rotate: [0, 8, -8, 0] }}
            transition={{
              repeat: Infinity,
              duration: 7 + i,
              delay: b.delay,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              width: b.w,
              height: b.h,
              borderRadius: "50%",
              background: b.bg,
              top: b.top,
              bottom: b.bottom,
              left: b.left,
              right: b.right,
              pointerEvents: "none",
            }}
          />
        ))}
        <motion.p
          style={S.heroBadge}
          variants={fadeDown}
          initial="hidden"
          animate="show"
        >
          ✦ Where Artists Belong ✦
        </motion.p>
        <motion.h1
          style={S.heroTitle}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9 }}
        >
          Join the <span style={{ color: "var(--gold,#c9982a)" }}>Artesa</span>
          <br />
          Community
        </motion.h1>
        <motion.p
          style={S.heroSub}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          A sacred creative space where artists create, inspire, and connect. Be
          part of something that heals, grows, and transforms through art.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          style={{
            marginTop: 48,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <motion.a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            whileHover={{
              scale: 1.06,
              boxShadow: "0 16px 40px rgba(201,152,42,0.55)",
            }}
            whileTap={{ scale: 0.97 }}
            style={{
              padding: "16px 40px",
              borderRadius: "50px",
              border: "none",
              background: "linear-gradient(135deg,#c9982a,#f0c84a)",
              color: "#1a0a1e",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-block",
              letterSpacing: 0.5,
              boxShadow: "0 8px 30px rgba(201,152,42,0.4)",
            }}
          >
            Join on WhatsApp
          </motion.a>
          <motion.a
            href="#how-to-join"
            whileHover={{
              borderColor: "var(--gold,#c9982a)",
              color: "var(--gold,#c9982a)",
            }}
            style={{
              padding: "16px 40px",
              borderRadius: "50px",
              border: "2px solid rgba(255,255,255,0.3)",
              background: "transparent",
              color: "#fff",
              fontWeight: 500,
              fontSize: "1rem",
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-block",
              letterSpacing: 0.5,
              transition: "all 0.3s",
            }}
          >
            How It Works
          </motion.a>
        </motion.div>
      </section>
      <div style={S.statsBar}>
        {stats.map((s, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div style={{ textAlign: "center" }}>
              <div style={S.statNum}>{s.num}</div>
              <div style={S.statLabel}>{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <section style={S.sectionWarm}>
        <Reveal>
          <p style={S.sectionLabel}>✦ Why Artesa</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 style={S.sectionTitle}>
            A Community That{" "}
            <span style={{ color: "var(--coral,#e8503a)" }}>Heals</span> Through
            Art
          </h2>
        </Reveal>
        <motion.div
          style={S.whyGrid}
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
        >
          {whyCards.map((c, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ y: -10, boxShadow: "0 24px 50px rgba(0,0,0,0.13)" }}
              style={{
                ...S.whyCard,
                borderTop: `4px solid ${c.accent}`,
              }}
            >
              <div style={{ fontSize: "2.8rem", marginBottom: 18 }}>
                {c.icon}
              </div>
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond',serif",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "var(--ink,#1a0a1e)",
                  marginBottom: 12,
                }}
              >
                {c.title}
              </h3>
              <p
                style={{
                  color: "#6a5a7a",
                  lineHeight: 1.8,
                  fontSize: "0.95rem",
                  fontWeight: 300,
                }}
              >
                {c.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </section>
      <section style={S.quoteSec}>
        <div
          style={{
            position: "absolute",
            top: -40,
            left: "4%",
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: "28rem",
            color: "rgba(201,152,42,0.05)",
            fontStyle: "italic",
            lineHeight: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          "
        </div>
        <Reveal>
          <p style={S.quoteText}>
            <span style={{ color: "var(--gold,#c9982a)" }}>
              Art is not what you see,
            </span>{" "}
            but what you make others feel — and in this community, we feel{" "}
            <span style={{ color: "var(--gold,#c9982a)" }}>
              everything together.
            </span>
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p style={S.quoteAuthor}>— Artem Clava, Founder of Artesa</p>
        </Reveal>
      </section>
      <section style={S.sectionCream} id="how-to-join">
        <Reveal style={{ textAlign: "center" }}>
          <p style={S.sectionLabel}>✦ Simple Steps</p>
          <h2 style={S.sectionTitle}>
            How to <span style={{ color: "var(--coral,#e8503a)" }}>Join</span>{" "}
            Artesa
          </h2>
        </Reveal>
        <motion.div
          style={S.stepsGrid}
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
        >
          {steps.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              style={{ textAlign: "center", padding: "0 16px 40px" }}
            >
              <motion.div
                whileHover={{ scale: 1.12, rotate: 5 }}
                style={S.stepNum}
              >
                {s.num}
              </motion.div>
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond',serif",
                  fontSize: "1.3rem",
                  fontWeight: 700,
                  color: "var(--ink,#1a0a1e)",
                  marginBottom: 10,
                }}
              >
                {s.title}
              </h3>
              <p
                style={{
                  color: "#7a6a8a",
                  fontSize: "0.9rem",
                  lineHeight: 1.8,
                  fontWeight: 300,
                }}
              >
                {s.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </section>
      <section style={S.sectionWarm}>
        <Reveal style={{ textAlign: "center" }}>
          <p style={S.sectionLabel}>✦ Open to All</p>
          <h2 style={S.sectionTitle}>
            Every Artist is{" "}
            <span style={{ color: "var(--coral,#e8503a)" }}>Welcome</span>
          </h2>
        </Reveal>
        <motion.div
          style={S.whoGrid}
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
        >
          {whoCards.map((w, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{
                y: -8,
                borderColor: "var(--gold,#c9982a)",
                boxShadow: "0 16px 40px rgba(201,152,42,0.2)",
              }}
              style={{
                ...S.whoCard,
                border: "2px solid transparent",
                transition: "border-color 0.3s,box-shadow 0.3s",
              }}
            >
              <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>
                {w.emoji}
              </div>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond',serif",
                  fontSize: "1.15rem",
                  fontWeight: 700,
                  color: "var(--ink,#1a0a1e)",
                }}
              >
                {w.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>
      <section style={S.ctaFinal}>
        <div
          style={{
            position: "absolute",
            right: -80,
            bottom: -80,
            fontSize: "20rem",
            color: "rgba(201,152,42,0.04)",
            fontFamily: "'Satisfy',cursive",
            pointerEvents: "none",
          }}
        >
          ✦
        </div>
        <Reveal>
          <div style={S.artesaTag}>Artesa by Artem Clava</div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: "clamp(2.5rem,6vw,5rem)",
              fontStyle: "italic",
              fontWeight: 700,
              color: "#fff",
              marginBottom: 20,
            }}
          >
            Ready to{" "}
            <span style={{ color: "var(--gold,#c9982a)" }}>Create</span>
            <br />
            with Us?
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "1.1rem",
              fontWeight: 300,
              maxWidth: 500,
              margin: "0 auto 48px",
              lineHeight: 1.8,
            }}
          >
            381 artists and counting. The canvas is wide open. Your place in
            Artesa is waiting.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <motion.a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            whileHover={{
              y: -6,
              boxShadow: "0 20px 50px rgba(37,211,102,0.55)",
            }}
            whileTap={{ scale: 0.97 }}
            style={S.waBtn}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Join Artesa on WhatsApp
          </motion.a>
        </Reveal>
      </section>
    </motion.div>
  );
}
export default Career;
